import { useGetQuery } from '../../graphql';

export interface WorkspaceEmailSettings {
  emailEnabled: boolean;
  emailSlug: string | null;
  emailAutoResponderText: string | null;
}

/**
 * Reads the current workspace's email provisioning state
 * (Communication settings — GRIPP #2968 / plan 0024 Phase 1, autoresponder
 * text added in the plan 0024 addendum).
 */
export const useWorkspaceEmailSettings = (workspaceId?: string) => {
  const { data, isLoading, refetch } = useGetQuery({
    modelName: 'workspace',
    query: GET_WORKSPACE_COMMUNICATION_SETTINGS,
    id: workspaceId ?? '',
    options: {
      enabled: !!workspaceId,
    },
  });

  const workspace = data as WorkspaceEmailSettings | undefined;

  return {
    emailEnabled: workspace?.emailEnabled ?? false,
    emailSlug: workspace?.emailSlug ?? null,
    emailAutoResponderText: workspace?.emailAutoResponderText ?? null,
    isLoading,
    refetch,
  };
};

/**
 * Shared by useWorkspaceEmailSettings, useWorkspacePhoneNumber AND
 * useWorkspaceSmsEnabled: all `useGetQuery({ modelName: 'workspace', id })`
 * hooks share one React Query cache key, so only one queryFn fetches when
 * they mount together — fields outside the winning document silently read
 * `undefined`. One document, the union of all three hooks' fields; add new
 * communication fields HERE.
 */
export const GET_WORKSPACE_COMMUNICATION_SETTINGS = `
  query GetWorkspaceCommunicationSettings($id: ID!) {
    getWorkspace(id: $id) {
      id
      emailEnabled
      emailSlug
      emailAutoResponderText
      phoneNumber
      smsEnabled
    }
  }
`;
